<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

const NEXXSEND_API_KEY = 'nxs_flow_k8v2m9p4x7';
const VICIDIAL_API = '/vicidial/agent-api.php';

const currentUser = useMapGetter('getCurrentUser');
const currentUserId = useMapGetter('getCurrentUserID');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');
const currentAccount = useMapGetter('getCurrentAccount');
const route = useRoute();

const loading = ref(true);
const error = ref('');
const iframeSrc = ref('');

async function init() {
  loading.value = true;
  error.value = '';

  const userId = currentUserId.value;
  const userName = currentUser.value?.name || currentUser.value?.available_name || 'Agente';
  const accountId = currentAccountId.value;
  const accountName = currentAccount.value?.name || 'Account ' + accountId;
  const role = currentRole.value || 'agent';

  if (!userId) {
    error.value = 'Sessao invalida';
    loading.value = false;
    return;
  }

  try {
    const params = new URLSearchParams({
      action: 'chatwoot_auth',
      api_key: NEXXSEND_API_KEY,
      chatwoot_id: String(userId),
      full_name: userName,
      account_id: String(accountId || ''),
      account_name: accountName,
      role: role,
    });
    const res = await fetch(VICIDIAL_API + '?' + params);
    const data = await res.json();
    if (data.error) {
      error.value = data.error;
      loading.value = false;
      return;
    }

    try {
      sessionStorage.setItem('flow_auto_auth', JSON.stringify({
        credentials: data.credentials,
        role: data.role,
      }));
    } catch (e) { /* private browsing */ }

    // A rota define qual tela do Flow abrir (ver meta.flowPath em routes.js).
    const flowPath = route.meta?.flowPath || '/admin';
    iframeSrc.value =
      '/discador/login?embed=1&next=' + encodeURIComponent(flowPath);
    loading.value = false;
  } catch (e) {
    error.value = 'Erro ao conectar com o servidor de telefonia';
    loading.value = false;
  }
}

onMounted(init);

// Discadora e Ura usam o mesmo componente. Sem isso, trocar de uma para a
// outra na sidebar nao remontaria o componente e o iframe ficaria na tela
// anterior.
watch(
  () => route.name,
  (to, from) => {
    if (to !== from) init();
  }
);
</script>

<template>
  <div class="dialer-wrapper">
    <div v-if="loading" class="dialer-state">
      <span class="i-lucide-loader-2 animate-spin" style="font-size: 28px; color: rgb(var(--blue-9))" />
      <p>Conectando a discadora...</p>
    </div>
    <div v-else-if="error" class="dialer-state">
      <span class="i-lucide-alert-circle" style="font-size: 28px; color: rgb(var(--red-9))" />
      <p>{{ error }}</p>
      <button class="dialer-retry" @click="init">Tentar novamente</button>
    </div>
    <iframe
      v-else
      :src="iframeSrc"
      class="dialer-iframe"
      allow="microphone; autoplay"
    />
  </div>
</template>

<style scoped>
.dialer-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.dialer-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgb(var(--slate-11));
  font-size: 15px;
}
.dialer-retry {
  margin-top: 8px;
  padding: 8px 20px;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-12));
  cursor: pointer;
  font-size: 14px;
}
.dialer-retry:hover {
  background: rgb(var(--slate-2));
}
.dialer-iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
}
</style>
